import React, { FC, useContext } from 'react';

import { ProjectGroupInfo, translateProjectStatus } from 'api-client';
import { cn, RouteDictionary } from 'utils';

import { CustomLink, MetricProgressBar, Money } from '../../../common';
import { buildGroupSegments, groupSizeLabel } from '../../projects/groupSegments';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';
import styles from './ProjectGroupPanel.module.scss';

/**
 * Что стоит в этой колонке выше — от этого зависит, с какой строки сетки начинается блок.
 * Без блока инвестирования и без блока резервирования он поднимается наверх колонки.
 */
export type ProjectGroupPanelPlacement = 'top' | 'underReserve' | 'underInvest';

interface Props {
  group: ProjectGroupInfo;
  placement: ProjectGroupPanelPlacement;
}

/**
 * Статус сбора по всей сделке, когда проект входит в группу: один большой платёж разбивают
 * на несколько проектов, и по отдельному проекту не видно, как идёт сбор по сделке целиком.
 *
 * Полоса разбита на сегменты — по одному на проект: ширина сегмента это доля проекта в
 * сумме сделки, заливка внутри сегмента — сколько собрано по этому проекту. Сегмент
 * текущего проекта выделен, остальные ведут на свои страницы.
 *
 * Стоит в колонке с блоком инвестирования, а широкая полоса на странице проекта остаётся
 * только про сбор по самому проекту.
 */
const ProjectGroupPanel: FC<Props> = ({ group, placement }) => {
  const currentProject = useContext(DetailedProjectContext).project;

  const otherMembers = group.members.filter((member) => member.uuid !== currentProject.uuid);

  return (
    <section
      className={cn(
        styles.root,
        placement === 'underReserve' && styles.underReserve,
        placement === 'underInvest' && styles.underInvest
      )}
    >
      <div className={styles.header}>
        <h2 className={styles.groupName}>{group.name}</h2>
        <div className={styles.count}>{groupSizeLabel(group.memberCount)}</div>
      </div>

      <MetricProgressBar
        currentValue={<Money money={group.totalInvestedSum} compact withPennies={false} />}
        totalValue={<Money money={group.totalDebtPrice} compact withPennies={false} />}
        currentLabel="Собрано"
        totalLabel="Сумма сбора"
        segments={buildGroupSegments(group, currentProject.uuid)}
      />

      <dl className={styles.stats}>
        <div className={styles.statRow}>
          <dt>Прогноз взыскания</dt>
          <dd>
            <Money money={group.totalRecoveryForecast} withPennies={false} />
          </dd>
        </div>
      </dl>

      {otherMembers.length > 0 && (
        <div className={styles.links}>
          <div className={styles.linksTitle}>Другие проекты группы</div>
          <ul className={styles.linksList}>
            {otherMembers.map((member) => (
              <li className={styles.linksItem} key={member.uuid}>
                <CustomLink
                  className={styles.link}
                  to={RouteDictionary.DETAILED_PROJECT}
                  params={{ uuid: member.uuid }}
                >
                  {member.name[0].text}
                </CustomLink>
                <div className={styles.linkMeta}>
                  <Money money={member.debtPrice} compact withPennies={false} />
                  <span className={styles.linkStatus}>{translateProjectStatus(member.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ProjectGroupPanel;
